import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/lib/quietToasts";
import "@/App.css";
import { AuthProvider, useAuth, hasAnyRole, isMaster, isCoworking } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Activar from "@/pages/Activar";
import Hoy from "@/pages/Hoy";
import Inicio from "@/pages/Inicio";
import Salas from "@/pages/Salas";
import Limites from "@/pages/Limites";
import ConfigurarSalas from "@/pages/ConfigurarSalas";
import UnidadesMasters from "@/pages/UnidadesMasters";
import ReglasUsoSalas from "@/pages/ReglasUsoSalas";
import RecepcionStaff from "@/pages/RecepcionStaff";
import Autorizados from "@/pages/Autorizados";
import ReservarSala from "@/pages/ReservarSala";
import MisReservas from "@/pages/MisReservas";
import ReservasOperaciones from "@/pages/ReservasOperaciones";
import ReservasDelDia from "@/pages/ReservasDelDia";
import ReportarIncidencia from "@/pages/ReportarIncidencia";
import MisIncidencias from "@/pages/MisIncidencias";
import IncidenciasKanban from "@/pages/IncidenciasKanban";
import Comunicaciones from "@/pages/Comunicaciones";
import BandejaAvisos from "@/pages/BandejaAvisos";
import CargarVisita from "@/pages/CargarVisita";
import MisVisitas from "@/pages/MisVisitas";
import VisitasHoy from "@/pages/VisitasHoy";
import Paqueteria from "@/pages/Paqueteria";
import MisPaquetes from "@/pages/MisPaquetes";
import MiPerfil from "@/pages/MiPerfil";

function HomeRedirect() {
    const { user } = useAuth();
    if (!user) return <Navigate to="/login" replace />;
    if (hasAnyRole(user, "operaciones")) return <Navigate to="/incidencias-kanban" replace />;
    if (hasAnyRole(user, "recepcion")) return <Navigate to="/visitas-hoy" replace />;
    if (hasAnyRole(user, "usuario_edificio")) return <Navigate to="/inicio" replace />;
    return <Navigate to="/login" replace />;
}

function P({ roles, children }) {
    return (
        <ProtectedRoute roles={roles}>
            <Layout>{children}</Layout>
        </ProtectedRoute>
    );
}

function MasterOnly({ children }) {
    const { user } = useAuth();
    if (hasAnyRole(user, "operaciones")) return children;
    if (!isMaster(user) || isCoworking(user)) {
        return <Navigate to="/" replace />;
    }
    return children;
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Toaster richColors position="top-right" />
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/activar" element={<Activar />} />
                    <Route path="/activar/:unidadId" element={<Activar />} />
                    <Route path="/" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />

                    {/* Operaciones */}
                    <Route path="/configurar-salas" element={<P roles={["operaciones"]}><ConfigurarSalas /></P>} />
                    <Route path="/salas" element={<Navigate to="/configurar-salas?tab=salas" replace />} />
                    <Route path="/limites" element={<Navigate to="/configurar-salas?tab=reglas" replace />} />
                    <Route path="/reglas-uso-salas" element={<Navigate to="/configurar-salas?tab=reglas" replace />} />
                    <Route path="/unidades-masters" element={<P roles={["operaciones", "recepcion"]}><UnidadesMasters /></P>} />
                    <Route path="/recepcion-staff" element={<P roles={["operaciones"]}><RecepcionStaff /></P>} />
                    <Route path="/reservas" element={<P roles={["operaciones", "recepcion"]}><ReservasOperaciones /></P>} />
                    <Route path="/incidencias-kanban" element={<P roles={["operaciones", "recepcion"]}><IncidenciasKanban /></P>} />
                    <Route path="/comunicaciones" element={<P roles={["operaciones", "recepcion"]}><Comunicaciones /></P>} />
                    <Route path="/visitas" element={<P roles={["operaciones"]}><VisitasHoy showAll /></P>} />

                    {/* Recepción */}
                    <Route path="/hoy" element={<P roles={["recepcion", "operaciones"]}><Hoy /></P>} />
                    <Route path="/reservas-del-dia" element={<P roles={["recepcion", "operaciones"]}><ReservasDelDia /></P>} />
                    <Route path="/visitas-hoy" element={<P roles={["recepcion", "operaciones"]}><VisitasHoy /></P>} />
                    <Route path="/incidencias-recepcion" element={<Navigate to="/incidencias-kanban" replace />} />

                    {/* Usuario edificio */}
                    <Route path="/inicio" element={<P roles={["usuario_edificio"]}><Inicio /></P>} />
                    <Route path="/reservar" element={<P roles={["usuario_edificio"]}><ReservarSala /></P>} />
                    <Route path="/mis-reservas" element={<P roles={["usuario_edificio"]}><MisReservas /></P>} />
                    <Route path="/reportar-incidencia" element={<P roles={["usuario_edificio"]}><ReportarIncidencia /></P>} />
                    <Route path="/mis-incidencias" element={<P roles={["usuario_edificio"]}><MisIncidencias /></P>} />
                    <Route path="/cargar-visita" element={<P roles={["usuario_edificio"]}><CargarVisita /></P>} />
                    <Route path="/mis-visitas" element={<P roles={["usuario_edificio"]}><MisVisitas /></P>} />
                    <Route path="/bandeja-avisos" element={<P roles={["usuario_edificio"]}><BandejaAvisos /></P>} />
                    <Route path="/autorizados" element={<P roles={["usuario_edificio"]}><MasterOnly><Autorizados /></MasterOnly></P>} />

                    {/* Perfil disponible para todos los roles autenticados */}
                    <Route path="/mi-perfil" element={<P><MiPerfil /></P>} />

                    {/* Paquetería */}
                    <Route path="/paqueteria" element={<P roles={["operaciones", "recepcion"]}><Paqueteria /></P>} />
                    <Route path="/mis-paquetes" element={<P roles={["usuario_edificio"]}><MisPaquetes /></P>} />

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}
